import { SxProps } from '@mui/material';

import usePlanHandler from '@webclient/components/plans/hooks/usePlanHandler';
import StyledChip from '@webclient/components/plans/kinds/styles';
import SubscriptionOptionsTab from '@webclient/components/plans/subscription-options-tab';
import Card from '@webclient/components/upgrade-modal/components/card';
import WithFeaturedBadge from '@webclient/components/upgrade-modal/components/with-featured-badge';

import useToggleState from '@webclient/hooks/useToggleState';

import { useTranslate } from '@tolgee/react';

type Props = {
  sx?: SxProps;
};

export default WithFeaturedBadge(function GeniusSearch({ sx }: Props) {
  const { state: isBillingCycleMonthly, toggle: toggleBillingCycle } =
    useToggleState(false);
  const billingCycle = isBillingCycleMonthly ? 'monthly' : 'yearly';

  const { t } = useTranslate();
  const { handleRedirectUserToRequiredPlan, isPending } = usePlanHandler();

  return billingCycle === 'monthly' ? (
    <Card
      sx={sx}
      title={t('layout.upgrade.plans.geniusSearch.title', 'Genius Search Plan')}
      subtitle={t(
        'layout.upgrade.plans.geniusSearch.subtitle',
        'Tailored for Professionals',
      )}
      priceInfo={{
        priceLine: t(
          'layout.upgrade.plans.geniusSearch.monthly.priceInfo.priceLine',
          '24.95',
        ),
        creditLine: t(
          'layout.upgrade.plans.geniusSearch.monthly.priceInfo.creditLine.segment2',
          'billed monthly',
        ),
        cycle: t(
          'layout.upgrade.plans.geniusSearch.monthly.priceInfo.creditLine.segment1',
          'Month',
        ),
      }}
      keyFeatures={[
        t(
          'layout.upgrade.plans.geniusSearch.keyFeatures.0',
          'More precise results with Genius Search',
        ),
        t(
          'layout.upgrade.plans.geniusSearch.keyFeatures.1',
          'Invite and work with 20 users in each of your 3 Knowledge Bases',
        ),
        t(
          'layout.upgrade.plans.geniusSearch.keyFeatures.2',
          '500MB Knowledge storage**',
        ),
        t(
          'layout.upgrade.plans.geniusSearch.keyFeatures.3',
          'Credits rollover each month',
        ),
      ]}
      boldFeature={t(
        'layout.upgrade.plans.geniusSearch.monthly.boldFeature',
        '20,000 Credits per month ≈',
      )}
      badges={[
        t('layout.upgrade.plans.geniusSearch.monthly.badges.0', '2,000 Docs'),
        t(
          'layout.upgrade.plans.geniusSearch.monthly.badges.1',
          '2,000 Searches',
        ),
      ]}
      button={{
        onClick: handleRedirectUserToRequiredPlan('geniusSearchMonthly'),
        text: t('layout.upgrade.plans.geniusSearch.button', 'Power Up Now'),
        isLoading: isPending,
      }}
    >
      <SubscriptionOptionsTab
        billingCycle={billingCycle}
        toggleCycle={toggleBillingCycle}
      />
    </Card>
  ) : (
    <Card
      title={t('layout.upgrade.plans.geniusSearch.title', 'Genius Search Plan')}
      subtitle={t(
        'layout.upgrade.plans.geniusSearch.subtitle',
        'Tailored for Professionals',
      )}
      priceInfo={{
        priceLine: t(
          'layout.upgrade.plans.geniusSearch.yearly.priceInfo.priceLine',
          '19.95',
        ),
        creditLine: `$${t(
          'layout.upgrade.plans.geniusSearch.yearly.priceInfo.creditLine.segment2',
          '239.40 billed yearly',
        )}`,
        cycle: t(
          'layout.upgrade.plans.geniusSearch.yearly.priceInfo.creditLine.segment1',
          'Month',
        ),
      }}
      keyFeatures={[
        t(
          'layout.upgrade.plans.geniusSearch.keyFeatures.0',
          'More precise results with Genius Search',
        ),
        t(
          'layout.upgrade.plans.geniusSearch.keyFeatures.1',
          'Invite and work with 20 users in each of your 3 Knowledge Bases',
        ),
        t(
          'layout.upgrade.plans.geniusSearch.keyFeatures.2',
          '500MB Knowledge storage**',
        ),
        t(
          'layout.upgrade.plans.geniusSearch.keyFeatures.3',
          'Credits rollover each month',
        ),
      ]}
      boldFeature={t(
        'layout.upgrade.plans.geniusSearch.yearly.boldFeature',
        '240,000 credits per year ≈',
      )}
      badges={[
        t('layout.upgrade.plans.geniusSearch.yearly.badges.0', '60,000 Docs'),
        t(
          'layout.upgrade.plans.geniusSearch.yearly.badges.1',
          '60,000 Searches',
        ),
      ]}
      button={{
        onClick: handleRedirectUserToRequiredPlan('geniusSearchYearly'),
        text: t(
          'layout.upgrade.plans.geniusSearch.button',
          'Subscribe and Save',
        ),
        isLoading: isPending,
      }}
      DiscountBadge={
        <StyledChip
          label="Save 20%"
          variant="filled"
          size="small"
          color="brand"
        />
      }
    >
      <SubscriptionOptionsTab
        billingCycle={billingCycle}
        toggleCycle={toggleBillingCycle}
      />
    </Card>
  );
});
